import React, { useState, useEffect } from 'react';
import { Calendar, Clock, User, ChevronLeft, CheckCircle2, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { getSpecialties, getAvailableDates, getProfessionalsByDate, getProfessionalSlots, bookAppointmentFromChat, getAppointmentsByConversation } from '../services/api';

function AppointmentBooking({ conversationId, contactName }) {
  const [expanded, setExpanded] = useState(false);
  const [step, setStep] = useState('specialty');
  const [specialties, setSpecialties] = useState([]);
  const [selectedSpecialty, setSelectedSpecialty] = useState('');
  const [availableDates, setAvailableDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [professionals, setProfessionals] = useState([]);
  const [selectedProfessional, setSelectedProfessional] = useState(null);
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState('');
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState('');

  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  useEffect(() => {
    loadHistory();
  }, [conversationId]);

  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const res = await getAppointmentsByConversation(conversationId);
      setHistory(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleOpen = () => {
    setExpanded(true);
    if (specialties.length === 0) loadSpecialties();
  };

  const loadSpecialties = async () => {
    setLoading(true);
    try {
      const res = await getSpecialties();
      setSpecialties(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectSpecialty = async (specialty) => {
    setSelectedSpecialty(specialty);
    setLoading(true);
    setError('');
    try {
      const res = await getAvailableDates(specialty);
      setAvailableDates(res.data);
      setStep('date');
    } catch (err) {
      setError('Erro ao buscar datas disponíveis');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectDate = async (date) => {
    setSelectedDate(date);
    setLoading(true);
    setError('');
    try {
      const res = await getProfessionalsByDate(selectedSpecialty, date);
      setProfessionals(res.data);
      setStep('professional');
    } catch (err) {
      setError('Erro ao buscar profissionais');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectProfessional = async (professional) => {
    setSelectedProfessional(professional);
    setLoading(true);
    setError('');
    try {
      const res = await getProfessionalSlots(professional.id, selectedDate);
      setSlots(res.data);
      setStep('slot');
    } catch (err) {
      setError('Erro ao buscar horários');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    setConfirming(true);
    setError('');
    try {
      await bookAppointmentFromChat({
        professional_id: selectedProfessional.id,
        date: selectedDate,
        start_time: selectedSlot,
        conversation_id: conversationId,
        contact_name: contactName
      });
      setStep('confirmed');
      loadHistory();
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao confirmar agendamento');
      if (err.response?.status === 409) {
        setStep('slot');
        handleSelectProfessional(selectedProfessional);
      }
    } finally {
      setConfirming(false);
    }
  };

  const resetAll = () => {
    setStep('specialty');
    setSelectedSpecialty('');
    setSelectedDate('');
    setSelectedProfessional(null);
    setSelectedSlot('');
    setError('');
    setExpanded(false);
  };

  const formatDateLabel = (dateStr) => {
    const [year, month, day] = String(dateStr).slice(0, 10).split('-').map(Number);
    const d = new Date(year, month - 1, day);
    return d.toLocaleDateString('pt-BR', { weekday: 'short', day: '2-digit', month: '2-digit' });
  };

  const formatHistoryDate = (isoStr) => {
    const match = String(isoStr).match(/(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
    if (!match) return isoStr;
    const [, year, month, day, hour, min] = match;
    const d = new Date(Number(year), Number(month) - 1, Number(day));
    return `${d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })} às ${hour}:${min}`;
  };

  const statusLabel = {
    scheduled: { label: 'Agendado', color: 'bg-blue-100 text-blue-700' },
    confirmed: { label: 'Confirmado', color: 'bg-green-100 text-green-700' },
    completed: { label: 'Concluído', color: 'bg-gray-100 text-gray-700' },
    cancelled: { label: 'Cancelado', color: 'bg-red-100 text-red-700' },
    no_show: { label: 'Faltou', color: 'bg-amber-100 text-amber-700' }
  };

  return (
    <div className="flex-1 overflow-y-auto flex flex-col">
      {/* Botão / painel de agendamento */}
      <div className="p-4 border-b border-gray-100 dark:border-slate-700">
        {!expanded ? (
          <button
            onClick={handleOpen}
            className="w-full flex items-center justify-between gap-2 px-4 py-3 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 rounded-xl text-sm font-bold hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors"
          >
            <span className="flex items-center gap-2">
              <Calendar size={18} />
              Agendar Consulta
            </span>
            <ChevronDown size={16} />
          </button>
        ) : (
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-gray-900 dark:text-slate-100 text-sm flex items-center gap-2">
                <Calendar size={18} className="text-green-600" />
                Agendar Consulta
              </h3>
              <button onClick={() => setExpanded(false)} className="text-gray-400 hover:text-gray-600">
                <ChevronUp size={18} />
              </button>
            </div>

            {step !== 'specialty' && step !== 'confirmed' && (
              <button
                onClick={() => {
                  if (step === 'date') setStep('specialty');
                  else if (step === 'professional') setStep('date');
                  else if (step === 'slot') setStep('professional');
                }}
                className="flex items-center gap-1 text-xs text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200 mb-3"
              >
                <ChevronLeft size={14} />
                Voltar
              </button>
            )}

            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-2 mb-3">
                <p className="text-xs text-red-700 dark:text-red-400">{error}</p>
              </div>
            )}

            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 size={24} className="animate-spin text-green-600" />
              </div>
            ) : (
              <>
                {step === 'specialty' && (
                  <div className="space-y-2">
                    <p className="text-xs text-gray-500 dark:text-slate-400 mb-2">Escolha a especialidade</p>
                    {specialties.length === 0 ? (
                      <p className="text-xs text-gray-400 dark:text-slate-500">Nenhuma especialidade cadastrada ainda.</p>
                    ) : (
                      specialties.map(sp => (
                        <button
                          key={sp}
                          onClick={() => handleSelectSpecialty(sp)}
                          className="w-full text-left px-3 py-2.5 bg-gray-50 dark:bg-slate-700 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-xl text-sm font-medium text-gray-700 dark:text-slate-200 transition-colors"
                        >
                          {sp}
                        </button>
                      ))
                    )}
                  </div>
                )}

                {step === 'date' && (
                  <div className="space-y-2">
                    <p className="text-xs text-gray-500 dark:text-slate-400 mb-2">
                      {selectedSpecialty} — escolha o dia
                    </p>
                    {availableDates.length === 0 ? (
                      <p className="text-xs text-gray-400 dark:text-slate-500">Nenhum dia disponível no momento.</p>
                    ) : (
                      <div className="grid grid-cols-2 gap-2">
                        {availableDates.map(date => (
                          <button
                            key={date}
                            onClick={() => handleSelectDate(date)}
                            className="px-3 py-2.5 bg-gray-50 dark:bg-slate-700 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-xl text-xs font-medium text-gray-700 dark:text-slate-200 transition-colors capitalize"
                          >
                            {formatDateLabel(date)}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {step === 'professional' && (
                  <div className="space-y-2">
                    <p className="text-xs text-gray-500 dark:text-slate-400 mb-2">
                      {formatDateLabel(selectedDate)} — escolha o profissional
                    </p>
                    {professionals.length === 0 ? (
                      <p className="text-xs text-gray-400 dark:text-slate-500">Nenhum profissional disponível nesse dia.</p>
                    ) : (
                      professionals.map(prof => (
                        <button
                          key={prof.id}
                          onClick={() => handleSelectProfessional(prof)}
                          className="w-full flex items-center gap-3 px-3 py-2.5 bg-gray-50 dark:bg-slate-700 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-xl transition-colors text-left"
                        >
                          <div
                            className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                            style={{ backgroundColor: prof.color || '#4f46e5' }}
                          >
                            {prof.name?.charAt(0) || <User size={14} />}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-800 dark:text-slate-100">{prof.name}</p>
                            <p className="text-[10px] text-gray-500 dark:text-slate-400">{prof.free_slots_count} horário(s) livre(s)</p>
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                )}

                {step === 'slot' && (
                  <div className="space-y-2">
                    <p className="text-xs text-gray-500 dark:text-slate-400 mb-2">
                      {selectedProfessional?.name} — escolha o horário
                    </p>
                    {slots.length === 0 ? (
                      <p className="text-xs text-gray-400 dark:text-slate-500">Nenhum horário livre.</p>
                    ) : (
                      <div className="grid grid-cols-3 gap-2 mb-4">
                        {slots.map(slot => (
                          <button
                            key={slot.start}
                            onClick={() => setSelectedSlot(slot.start)}
                            className={`px-2 py-2 rounded-lg text-xs font-medium transition-colors ${
                              selectedSlot === slot.start
                                ? 'bg-green-600 text-white'
                                : 'bg-gray-50 dark:bg-slate-700 text-gray-700 dark:text-slate-200 hover:bg-green-50 dark:hover:bg-green-900/20'
                            }`}
                          >
                            {slot.start}
                          </button>
                        ))}
                      </div>
                    )}
                    {selectedSlot && (
                      <button
                        onClick={handleConfirm}
                        disabled={confirming}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-xl text-sm font-bold hover:bg-green-700 disabled:opacity-50 transition-all"
                      >
                        {confirming ? <Loader2 size={16} className="animate-spin" /> : <Clock size={16} />}
                        {confirming ? 'Confirmando...' : `Confirmar ${selectedSlot}`}
                      </button>
                    )}
                  </div>
                )}

                {step === 'confirmed' && (
                  <div className="text-center py-6">
                    <CheckCircle2 size={40} className="mx-auto text-green-600 mb-3" />
                    <p className="text-sm font-bold text-gray-900 dark:text-slate-100 mb-1">Agendado com sucesso!</p>
                    <p className="text-xs text-gray-500 dark:text-slate-400 mb-4">
                      {selectedProfessional?.name} — {formatDateLabel(selectedDate)} às {selectedSlot}
                    </p>
                    <button
                      onClick={resetAll}
                      className="text-xs text-green-600 dark:text-green-400 font-medium hover:underline"
                    >
                      Fechar
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Histórico de agendamentos dessa conversa */}
      <div className="p-4 flex-1 overflow-y-auto">
        <p className="text-xs font-bold text-gray-500 dark:text-slate-400 uppercase mb-2">Histórico de Agendamentos</p>
        {loadingHistory ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 size={20} className="animate-spin text-gray-400" />
          </div>
        ) : history.length === 0 ? (
          <p className="text-xs text-gray-400 dark:text-slate-500">Nenhum agendamento ainda.</p>
        ) : (
          <div className="space-y-2">
            {history.map(apt => (
              <div key={apt.id} className="bg-gray-50 dark:bg-slate-900/50 border border-gray-100 dark:border-slate-700 rounded-xl p-3">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-6 h-6 rounded-full flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0"
                      style={{ backgroundColor: apt.professional_color || '#4f46e5' }}
                    >
                      {apt.professional_name?.charAt(0) || '?'}
                    </div>
                    <p className="text-xs font-bold text-gray-800 dark:text-slate-100">{apt.professional_name || 'Profissional'}</p>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${statusLabel[apt.status]?.color || 'bg-gray-100 text-gray-600'}`}>
                    {statusLabel[apt.status]?.label || apt.status}
                  </span>
                </div>
                <p className="text-[11px] text-gray-500 dark:text-slate-400 ml-8">{apt.specialty}</p>
                <p className="text-xs text-gray-600 dark:text-slate-300 ml-8 mt-1 flex items-center gap-1">
                  <Clock size={11} />
                  {formatHistoryDate(apt.scheduled_at)}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default AppointmentBooking;
